import gsap from 'gsap'

/**
 * Landing splash → hero intro sequence, played once on load:
 *
 *   1. the centred splash logo fades in, holds, then flies up + scales down onto
 *      the header logo while the dark splash dissolves (hand-off to the real
 *      header logo is seamless because the flight lands exactly on it);
 *   2. the header navigation and the hero's right-hand decoration panel + chevron
 *      pattern stagger in together;
 *   3. the hero headline and the scroll cue rise in together.
 *
 * Page scroll is paused for the ~4s run so the reveal reads as one piece. The
 * opaque splash covers the header + hero the whole time, so the targets can start
 * hidden without any flash.
 *
 * We use a measured GSAP transform (not the View Transitions API) so the logo
 * hand-off composes with the same timeline as the rest and stays consistent
 * cross-browser. Respects prefers-reduced-motion: everything is shown at once and
 * the splash is removed with no motion.
 *
 * @param {import('lenis').default|null} [lenis] smooth-scroll instance to pause
 * @returns {gsap.core.Timeline|undefined}
 */
export function initIntro(lenis) {
  const splash = document.getElementById('splash')
  if (!splash) return

  const bg = document.getElementById('splash-bg')
  const splashLogo = document.getElementById('splash-logo')
  const headerLogo = document.getElementById('header-logo')
  const navItems = gsap.utils.toArray('#site-header nav a, #site-header a[href="#register"], #nav-toggle')
  const deco = document.getElementById('hero-deco')
  const pattern = document.querySelector('#hero-deco .hero-pattern')
  const scrollCue = document.getElementById('hero-scroll')
  const heroText = gsap.utils.toArray('#top .hero-copy > *')

  const revealTargets = [headerLogo, ...navItems, deco, pattern, scrollCue, ...heroText].filter(Boolean)

  // Prime the hidden/offset start states (covered by the splash — no flash).
  gsap.set(revealTargets, { autoAlpha: 0 })
  gsap.set(heroText, { y: 30 })
  gsap.set(navItems, { y: -12 })
  gsap.set(scrollCue, { y: 20 })
  gsap.set(deco, { xPercent: 100 })
  gsap.set(pattern, { y: 40 })
  gsap.set(splashLogo, { autoAlpha: 0 })

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    gsap.set(revealTargets, { autoAlpha: 1, clearProps: 'transform' })
    splash.remove()
    return
  }

  // Start at the top so the splash always lifts to reveal the hero.
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual'
  window.scrollTo(0, 0)
  lenis?.stop()

  const tl = gsap.timeline({
    defaults: { ease: 'power3.out' },
    onComplete: () => {
      splash.remove()
      lenis?.start()
    },
  })

  tl
    // 1 — logo fades in centre-screen, holds, then flies onto the header logo as
    //     the dark splash dissolves. x/y/scale are measured when the flight
    //     starts (functions), by which point the SVG is loaded and laid out.
    .to(splashLogo, { autoAlpha: 1, duration: 1.2, ease: 'power2.out' })
    .addLabel('lift', '+=0.35')
    .to(
      splashLogo,
      {
        duration: 1,
        ease: 'power3.inOut',
        x: () => centreDelta(splashLogo, headerLogo).x,
        y: () => centreDelta(splashLogo, headerLogo).y,
        scale: () => headerLogo.getBoundingClientRect().height / splashLogo.getBoundingClientRect().height,
      },
      'lift'
    )
    .to(bg, { autoAlpha: 0, duration: 1, ease: 'power2.inOut' }, 'lift')
    .set(headerLogo, { autoAlpha: 1 }, 'lift+=0.9') // real header logo takes over

    // 2 — header nav + hero decoration/pattern stagger in together.
    .addLabel('reveal', 'lift+=0.7')
    .to(navItems, { autoAlpha: 1, y: 0, duration: 0.6, stagger: 0.1 }, 'reveal')
    .to(deco, { autoAlpha: 1, xPercent: 0, duration: 0.9 }, 'reveal')
    .to(pattern, { autoAlpha: 1, y: 0, duration: 0.8 }, 'reveal+=0.15')

    // 3 — hero headline + scroll cue rise in together.
    .addLabel('content', 'reveal+=0.4')
    .to(heroText, { autoAlpha: 1, y: 0, duration: 0.9, stagger: 0.15 }, 'content')
    .to(scrollCue, { autoAlpha: 1, y: 0, duration: 0.8 }, 'content+=0.1')

  return tl
}

/** Translation that moves `from`'s centre onto `to`'s centre (current rects). */
function centreDelta(from, to) {
  const a = from.getBoundingClientRect()
  const b = to.getBoundingClientRect()
  return {
    x: b.left + b.width / 2 - (a.left + a.width / 2),
    y: b.top + b.height / 2 - (a.top + a.height / 2),
  }
}
